import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBrain,
    faCode,
    faCubes,
    faLocationDot,
    faNetworkWired,
    faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import SEO from './SEO';

const services = [
    {
        title: 'Custom Software Development',
        text: 'Purpose-built business systems, portals, dashboards, admin panels, reporting tools, and internal workflow software.',
        icon: faCode
    },
    {
        title: 'SaaS Product Engineering',
        text: 'Architecture, MVP development, subscription platforms, customer portals, APIs, and production-ready product foundations.',
        icon: faCubes
    },
    {
        title: 'AI Workflow Automation',
        text: 'Automation for repetitive business operations using structured workflows, integrations, assistants, and decision logic.',
        icon: faBrain
    },
    {
        title: 'Cloud, APIs & Integrations',
        text: 'Secure API development, third-party integrations, cloud deployment support, CI/CD, monitoring, and scalable backend systems.',
        icon: faNetworkWired
    }
];

const locationConfigs = {
    morbi: {
        locationName: 'Morbi',
        areaType: 'City',
        canonicalPath: '/software-development-company-in-morbi',
        title: 'Software Development Company in Morbi | IT Company in Morbi | Anmatics',
        description: 'Anmatics is a software development and IT company in Morbi building custom software, SaaS platforms, AI workflow automation, enterprise applications, APIs, and cloud-ready systems.',
        kicker: 'Morbi Software Engineering',
        h1: 'Software Development Company in Morbi for Reliable Business Systems',
        intro: 'Anmatics helps Morbi businesses and product teams build custom software, SaaS platforms, enterprise web applications, AI workflow automation, APIs, and cloud-ready systems with structured engineering and clear delivery.',
        proofTitle: 'Branch Office - Morbi',
        proofAddress: '207, Second Floor, City Mall, Nr. Bhaktinagar Bypass, Sanala Road, Morbi, Gujarat - 363641',
        localHeading: 'Built for Morbi businesses that need more than a basic website.',
        localText: 'Many local searches look for an IT company in Morbi, but the real business need is often workflow clarity, software reliability, integration, automation, and long-term scalability.',
        industryHeading: 'Software for Morbi’s industrial and digital economy.',
        industryText: 'Morbi has strong manufacturing, ceramic, export, trading, and operations-driven businesses. Anmatics supports these teams with systems that reduce manual work, centralize data, improve visibility, and make business operations easier to scale.',
        faqHeading: 'Questions about working with Anmatics in Morbi.',
        address: {
            streetAddress: '207, Second Floor, City Mall, Nr. Bhaktinagar Bypass, Sanala Road',
            addressLocality: 'Morbi',
            addressRegion: 'Gujarat',
            postalCode: '363641',
            addressCountry: 'IN'
        }
    },
    rajkot: {
        locationName: 'Rajkot',
        areaType: 'City',
        canonicalPath: '/software-development-company-in-rajkot',
        title: 'Software Development Company in Rajkot | IT Company in Rajkot | Anmatics',
        description: 'Anmatics is a software development and IT company serving Rajkot with custom software, SaaS product engineering, AI automation, enterprise web apps, APIs, and cloud systems.',
        kicker: 'Rajkot Software Engineering',
        h1: 'Software Development Company in Rajkot for Scalable Digital Products',
        intro: 'Anmatics helps Rajkot businesses, founders, and operations teams build reliable custom software, SaaS platforms, AI workflow automation, enterprise applications, APIs, and cloud-ready systems.',
        proofTitle: 'Head Office - Rajkot',
        proofAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road, Rajkot - 360005',
        localHeading: 'Built for Rajkot companies that need dependable software engineering.',
        localText: 'Rajkot businesses often need more than a website: they need portals, dashboards, workflow systems, integrations, automation, and scalable product architecture.',
        industryHeading: 'Software for Rajkot’s business, manufacturing, and service economy.',
        industryText: 'Anmatics supports Rajkot teams with custom systems that improve operational visibility, replace scattered spreadsheets, connect departments, and help digital products move from idea to production.',
        faqHeading: 'Questions about working with Anmatics in Rajkot.',
        address: {
            streetAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road',
            addressLocality: 'Rajkot',
            addressRegion: 'Gujarat',
            postalCode: '360005',
            addressCountry: 'IN'
        }
    },
    gujarat: {
        locationName: 'Gujarat',
        areaType: 'AdministrativeArea',
        canonicalPath: '/software-development-company-in-gujarat',
        title: 'Software Development Company in Gujarat | IT Company in Gujarat | Anmatics',
        description: 'Anmatics is a software development and IT company in Gujarat building custom software, SaaS platforms, AI workflow automation, enterprise applications, APIs, and cloud systems.',
        kicker: 'Gujarat Software Engineering',
        h1: 'Software Development Company in Gujarat for SaaS, AI and Enterprise Systems',
        intro: 'Anmatics serves companies across Gujarat with custom software development, SaaS product engineering, enterprise web applications, AI workflow automation, cloud APIs, and long-term product support.',
        proofTitle: 'Serving Gujarat from Rajkot and Morbi',
        proofAddress: 'Head office in Rajkot with branch office in Morbi, Gujarat',
        localHeading: 'Built for Gujarat companies that need structured technology execution.',
        localText: 'From manufacturing and exports to SaaS products and service operations, Gujarat businesses need reliable systems that can automate workflows, integrate data, and scale without becoming difficult to maintain.',
        industryHeading: 'Software for Gujarat’s industrial, commercial, and digital businesses.',
        industryText: 'Anmatics helps Gujarat companies modernize operations, build digital products, automate manual work, and create cloud-ready systems for long-term growth.',
        faqHeading: 'Questions about working with Anmatics in Gujarat.',
        address: {
            streetAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road',
            addressLocality: 'Rajkot',
            addressRegion: 'Gujarat',
            postalCode: '360005',
            addressCountry: 'IN'
        }
    },
    india: {
        locationName: 'India',
        areaType: 'Country',
        canonicalPath: '/software-development-company-in-india',
        title: 'Software Development Company in India | SaaS, AI & Enterprise Apps | Anmatics',
        description: 'Anmatics is a software development company in India building custom software, SaaS platforms, AI workflow automation, enterprise applications, APIs, and cloud-ready systems.',
        kicker: 'India Software Engineering',
        h1: 'Software Development Company in India for Product-Grade Engineering',
        intro: 'Anmatics works with businesses and product teams across India to build custom software, SaaS platforms, AI workflow automation, enterprise applications, APIs, and cloud-ready systems.',
        proofTitle: 'Serving India from Gujarat',
        proofAddress: 'Head office in Rajkot with branch office in Morbi, Gujarat, India',
        localHeading: 'Built for Indian companies that need reliable software delivery.',
        localText: 'Anmatics supports Indian businesses that need strong architecture, practical automation, modern web applications, scalable APIs, and long-term product engineering instead of short-term patchwork.',
        industryHeading: 'Software for India’s growing SaaS, manufacturing, and service businesses.',
        industryText: 'Anmatics helps teams across India plan, build, modernize, and scale business systems with clean engineering, clear ownership, and production-ready delivery.',
        faqHeading: 'Questions about working with Anmatics in India.',
        address: {
            streetAddress: '203, Ikon Complex, Opp. Sun City Apartment, Sadhu Vaswani Road',
            addressLocality: 'Rajkot',
            addressRegion: 'Gujarat',
            postalCode: '360005',
            addressCountry: 'IN'
        }
    }
};

const industries = [
    'Ceramic and manufacturing businesses',
    'Export and trading companies',
    'SaaS founders and product teams',
    'Operations-heavy service businesses',
    'Paper, packaging, and industrial workflows',
    'Businesses modernizing manual processes'
];

const buildShortAnswer = (config) =>
    `Anmatics is a software development and IT company serving ${config.locationName}. It builds custom software, SaaS platforms, AI workflow automation, enterprise web applications, APIs, and cloud-ready systems for businesses that need reliable product-grade engineering.`;

const buildFaqs = (config) => [
    {
        question: `Which company provides software development in ${config.locationName}?`,
        answer: buildShortAnswer(config)
    },
    {
        question: `Does Anmatics provide software development services in ${config.locationName}?`,
        answer: `Yes. Anmatics provides custom software development, SaaS product engineering, AI workflow automation, web applications, APIs, and cloud-ready systems for businesses in ${config.locationName}.`
    },
    {
        question: `What services does Anmatics offer in ${config.locationName}?`,
        answer: 'Anmatics offers custom software development, SaaS product engineering, enterprise web applications, AI workflow automation, cloud API engineering, system modernization, and product maintenance.'
    },
    {
        question: `What makes Anmatics different from a typical IT company in ${config.locationName}?`,
        answer: 'Anmatics focuses on structured software engineering, scalable architecture, SaaS platforms, AI-enabled workflows, and long-term product reliability instead of only basic websites or generic digital services.'
    },
    {
        question: `Can Anmatics build custom software for businesses in ${config.locationName}?`,
        answer: 'Yes. Anmatics can build workflow platforms, dashboards, portals, ERP-adjacent tools, integrations, automation systems, and product-grade applications for growing businesses and product teams.'
    },
    {
        question: 'Can we work with Anmatics remotely?',
        answer: 'Yes. Anmatics can work through structured remote delivery, technical discovery calls, clear milestones, and regular engineering communication for clients across Gujarat and India.'
    }
];

const buildSchema = (config, canonicalPath) => {
    const url = `https://www.anmatics.com${canonicalPath}`;
    const faqs = buildFaqs(config);

    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'LocalBusiness',
                '@id': `${url}#localbusiness`,
                name: `Anmatics Technologies - ${config.locationName}`,
                url,
                image: 'https://www.anmatics.com/anmatics_logo_square_512.png',
                telephone: '+91-8866899005',
                priceRange: '$$',
                address: {
                    '@type': 'PostalAddress',
                    ...config.address
                },
                areaServed: [
                    {
                        '@type': config.areaType,
                        name: config.locationName
                    },
                    {
                        '@type': 'City',
                        name: 'Morbi'
                    },
                    {
                        '@type': 'City',
                        name: 'Rajkot'
                    },
                    {
                        '@type': 'AdministrativeArea',
                        name: 'Gujarat'
                    },
                    {
                        '@type': 'Country',
                        name: 'India'
                    }
                ],
                sameAs: [
                    'https://www.linkedin.com/company/anmatics-technologies',
                    'https://www.facebook.com/share/16xFv6aVpA/?mibextid=wwXIfr'
                ]
            },
            {
                '@type': 'WebPage',
                '@id': `${url}#webpage`,
                url,
                name: config.title,
                description: config.description,
                isPartOf: {
                    '@type': 'WebSite',
                    name: 'Anmatics',
                    url: 'https://www.anmatics.com'
                },
                about: {
                    '@id': `${url}#localbusiness`
                }
            },
            {
                '@type': 'ItemList',
                name: `Software development services in ${config.locationName}`,
                itemListElement: services.map((service, index) => ({
                    '@type': 'ListItem',
                    position: index + 1,
                    item: {
                        '@type': 'Service',
                        name: service.title,
                        provider: {
                            '@id': `${url}#localbusiness`
                        },
                        areaServed: {
                            '@type': config.areaType,
                            name: config.locationName
                        }
                    }
                }))
            },
            {
                '@type': 'FAQPage',
                mainEntity: faqs.map((faq) => ({
                    '@type': 'Question',
                    name: faq.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: faq.answer
                    }
                }))
            }
        ]
    };
};

const LocalSoftwareSEO = ({ location = 'morbi', canonicalPath }) => {
    const config = locationConfigs[location] || locationConfigs.morbi;
    const pagePath = canonicalPath || config.canonicalPath;
    const faqs = buildFaqs(config);
    const shortAnswer = buildShortAnswer(config);

    return (
        <>
            <SEO
                title={config.title}
                description={config.description}
                path={pagePath}
                jsonLd={buildSchema(config, pagePath)}
            />
            <section className="local-seo-hero">
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-7">
                            <span className="section-kicker">{config.kicker}</span>
                            <h1>{config.h1}</h1>
                            <p>{config.intro}</p>
                            <div className="local-hero-actions">
                                <a href="/contact" className="btn btn-gradient rounded-pill">Book a Consultation</a>
                                <a href="/services" className="btn btn-secondary-brand rounded-pill">Explore Services</a>
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div className="local-proof-panel">
                                <div className="local-answer-card">
                                    <span className="section-kicker">Short Answer</span>
                                    <p>{shortAnswer}</p>
                                </div>
                                <div className="local-proof-header">
                                    <span><FontAwesomeIcon icon={faLocationDot} /></span>
                                    <div>
                                        <strong>{config.proofTitle}</strong>
                                        <p>{config.proofAddress}</p>
                                    </div>
                                </div>
                                <div className="local-proof-grid">
                                    <div>
                                        <strong>Custom Software</strong>
                                        <span>Business systems</span>
                                    </div>
                                    <div>
                                        <strong>SaaS</strong>
                                        <span>Product platforms</span>
                                    </div>
                                    <div>
                                        <strong>AI</strong>
                                        <span>Workflow automation</span>
                                    </div>
                                    <div>
                                        <strong>Cloud</strong>
                                        <span>APIs and scale</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section">
                <div className="container">
                    <div className="row g-4 align-items-start">
                        <div className="col-lg-4">
                            <span className="section-kicker">IT Company</span>
                            <h2 className="section-title text-start">{config.localHeading}</h2>
                            <p className="section-subtitle text-start text-muted mb-0">{config.localText}</p>
                        </div>
                        <div className="col-lg-8">
                            <div className="local-service-grid">
                                {services.map((service) => (
                                    <article className="local-service-card" key={service.title}>
                                        <span><FontAwesomeIcon icon={service.icon} /></span>
                                        <h3>{service.title}</h3>
                                        <p>{service.text}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section local-seo-band">
                <div className="container">
                    <div className="row g-4 align-items-center">
                        <div className="col-lg-6">
                            <span className="section-kicker">Industries</span>
                            <h2 className="section-title text-start">{config.industryHeading}</h2>
                            <p className="text-muted">{config.industryText}</p>
                        </div>
                        <div className="col-lg-6">
                            <div className="local-industry-list">
                                {industries.map((industry) => (
                                    <div key={industry}>
                                        <FontAwesomeIcon icon={faShieldAlt} />
                                        <span>{industry}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section">
                <div className="container">
                    <div className="local-process-panel">
                        <div>
                            <span className="section-kicker">Delivery Approach</span>
                            <h2 className="section-title text-start">A structured way to plan, build, and improve software.</h2>
                        </div>
                        <div className="local-process-steps">
                            {['Discover', 'Architect', 'Build', 'Launch', 'Improve'].map((step, index) => (
                                <div key={step}>
                                    <span>{String(index + 1).padStart(2, '0')}</span>
                                    <strong>{step}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="local-seo-section local-faq-section">
                <div className="container">
                    <div className="row g-4">
                        <div className="col-lg-4">
                            <span className="section-kicker">FAQ</span>
                            <h2 className="section-title text-start">{config.faqHeading}</h2>
                        </div>
                        <div className="col-lg-8">
                            <div className="local-faq-list">
                                {faqs.map((faq) => (
                                    <article key={faq.question}>
                                        <h3>{faq.question}</h3>
                                        <p>{faq.answer}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default LocalSoftwareSEO;
